import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Printer, Ticket as TicketIcon, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";

export default function Ticket() {
  const { id } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["ticket", id],
    queryFn: () => api.get(`/registrations/${id}/ticket`).then((r) => r.data),
  });

  if (isLoading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" /></div>;
  if (isError || !data) return <div className="min-h-[60vh] grid place-items-center text-[#4A5D6B]">Ticket not found.</div>;

  const ticketUrl = `${window.location.origin}/ticket/${data.id}`;
  const paid = data.payment_status === "paid" || data.payment_status === "free";

  return (
    <div className="max-w-md mx-auto px-6 py-10" data-testid="ticket-page">
      <style>{`@media print{.no-print{display:none!important}body{background:#fff}}`}</style>
      <div className="eyw-card overflow-hidden border-2 border-[#144F75]/15">
        <div className="bg-[#144F75] text-[#F9F7F3] px-6 py-5 flex items-center gap-2">
          <TicketIcon size={22} /><h1 className="font-serif text-2xl">Your ticket</h1>
        </div>
        <div className="p-7 text-center">
          <h2 className="font-serif text-2xl text-[#1A242B]" data-testid="ticket-event">{data.event_title}</h2>
          <p className="text-[#4A5D6B] mt-1">{[data.date, data.start_time].filter(Boolean).join(" · ")}</p>
          {data.location && <p className="text-sm text-[#8FA89B]">{data.location}</p>}

          <div className="my-6 flex justify-center">
            <div className="p-4 bg-white rounded-2xl border border-[#E5E0D8]" data-testid="ticket-qr">
              <QRCodeSVG value={ticketUrl} size={196} fgColor="#144F75" level="M" />
            </div>
          </div>

          <div className="text-left space-y-2 border-t border-[#E5E0D8] pt-4">
            <Row label="Attendee" value={data.attendee} />
            {data.tier && <Row label="Tier" value={data.tier} />}
            <Row label="Price" value={data.price > 0 ? `${money(Number(data.price))}` : "Complimentary"} />
            <Row label="Payment" value={paid ? "Paid" : "Due at door"} />
          </div>

          {data.checked_in ? (
            <p className="mt-5 inline-flex items-center gap-1.5 text-[#5C8A72] font-medium" data-testid="ticket-checked-in"><CheckCircle2 size={17} /> Checked in</p>
          ) : (
            <p className="mt-5 text-xs text-[#8FA89B]">Show this QR code at the door for check-in.</p>
          )}
        </div>
      </div>
      <button onClick={() => window.print()} data-testid="ticket-print" className="eyw-btn w-full mt-5 no-print"><Printer size={16} /> Print / Save as PDF</button>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm"><span className="text-[#8FA89B]">{label}</span><span className="text-[#1A242B] font-medium text-right">{value}</span></div>
);
